<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import type { Counters } from '@autobid/ui/types/components/CarsList'
import { useOtherFeatures } from '@autobid/ui/composables/auction/useOtherFeatures'
import { useDictionary } from '@autobid/ui/composables/useDictionary'
import { injectFilterComposable } from '@autobid/ui/utils/carsList/injectFilterComposable'
import { computed, inject, watch } from 'vue'

interface Props {
  counters: Counters
}

const props = defineProps<Props>()

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')
const { dictionary, suspense } = useDictionary({ type: 'auctions' })
onServerPrefetch(suspense)

const useFilter = injectFilterComposable()
const {
  filters: { e223, e174 },
  toggleAll,
  areAllChecked,
} = useFilter()
const { otherFeatures: otherFeaturesNoFilterCounters } = useOtherFeatures(
  auctionStrapiData?.type === 'carList' ? 'auction' : 'searchList',
)

const damagesOptions = computed(() => {
  const optionsToRender = (dictionary.value?.e223.options ?? []).filter(
    el => otherFeaturesNoFilterCounters.value?.e223?.[el.id],
  )

  return optionsToRender
})
const damagesPossibleToCheck = computed(() => {
  return damagesOptions.value.filter((option) => {
    if (!props.counters?.e223?.[option.id]) {
      return false
    }

    return props.counters?.e223[option.id] > 0
  })
})
const allDamagesChecked = computed(() => {
  return areAllChecked(damagesPossibleToCheck.value, e223.value)
})

const allDamagesCounter = computed(() =>
  Object.values(props.counters.e223 ?? {}).reduce((a, b) => a + b, 0),
)

function handleFilterOtherFeature(isAdding: boolean, value: number) {
  const currentValue = e223.value || []

  e223.value = isAdding
    ? [...currentValue, value]
    : currentValue.filter(el => el !== value)
}

const checkedAndDisabledDamageIds = computed(() => {
  return dictionary.value?.e223?.options?.reduce((acc: string[], el) => {
    const isChecked = e223.value?.includes(Number(el.id))
    const isDisabled = !props.counters.e223?.[el.id]

    if (isDisabled && isChecked) {
      acc = [...acc, String(el.id)]
    }

    return acc
  }, []) || []
})

function uncheckDisabledDamages() {
  if (!checkedAndDisabledDamageIds.value.length) {
    return
  }

  e223.value = e223.value?.filter(
    el => !checkedAndDisabledDamageIds.value.includes(String(el)),
  )
}
watch(checkedAndDisabledDamageIds, uncheckDisabledDamages)
</script>

<template>
  <common-filters-dropdown
    v-if="dictionary && otherFeaturesNoFilterCounters.atLeastOneExists"
    card-key="otherFeatures"
  >
    <template #title>
      {{ $t('cars-list.filters.other-features') }}
    </template>

    <template #content>
      <common-filters-checkboxes-wrapper class="!mt-0">
        <template v-if="otherFeaturesNoFilterCounters.e223 && Object.keys(otherFeaturesNoFilterCounters.e223).length">
          <ElementsCheckbox
            key="e223"
            :checked="allDamagesChecked"
            :label-suffix="allDamagesCounter"
            :disabled="allDamagesCounter === 0"
            :label="$t('cars-list.filters.damaged')"
            :small="true"
            @click="
              () => (e223 = toggleAll(damagesPossibleToCheck, e223).map(Number))
            "
          />
          <common-filters-checkboxes-wrapper class="!mt-0 pl-2">
            <template
              v-for="{ id, label } in dictionary.e223.options"
              :key="id"
            >
              <ElementsCheckbox
                v-if="otherFeaturesNoFilterCounters.e223[id]"
                class="[&>*]:text-[#58585A]"
                :checked="Boolean(e223?.includes(Number(id)))"
                :label-suffix="counters.e223?.[id] ?? 0"
                :disabled="(counters.e223?.[id] ?? 0) === 0"
                :label="label"
                :small="true"
                @change="
                  (isAdding: boolean) =>
                    handleFilterOtherFeature(isAdding, Number(id))
                "
              />
            </template>
          </common-filters-checkboxes-wrapper>
        </template>

        <ElementsCheckbox
          v-if="otherFeaturesNoFilterCounters.e174"
          class="[&>*]:text-[#58585A]"
          :checked="Boolean(e174)"
          :label-suffix="counters.e174 ?? 0"
          :disabled="(counters.e174 ?? 0) === 0"
          :label="dictionary.e174.label"
          :small="true"
          uncheck-if-disabled
          @change="
            (isAdding: boolean) => {
              e174 = isAdding ? 1 : undefined
            }
          "
        />
      </common-filters-checkboxes-wrapper>
    </template>
  </common-filters-dropdown>
</template>
